import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { DotIcon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import { usePostHog } from 'src/lib/posthog';
import PreviewCard from './PreviewCard';
import SanitizedMarkdown from './SanitizedMarkdown';
import { formatDocumentAuthorityLabel } from 'src/features/wiki/constants/documentAuthority';
import Button from './Button';
import PremiumUpsellModal from './PremiumUpsellModal';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';

type HeaderSource = {
  title?: string;
  date?: string;
  publication_date?: string;
  authority?: string;
  jurisdiction?: string;
};

interface SourcePopoverProps {
  /** The markdown content to display */
  content: string;
  /** Shown in the content area while `content` is empty, e.g. a loading skeleton. */
  contentPlaceholder?: ReactNode;
  /** Optional classes for the markdown content container */
  contentClassName?: string;
  /** Optional source metadata shown in header */
  source?: HeaderSource;
  /** Optional page info shown in header */
  pageInfo?: string;
  /** The trigger element */
  children: ReactNode;
  /** Compact trigger layout (for citation pills) */
  compactTrigger?: boolean;
  /** Whether to show "click to view" in truncation message */
  clickable?: boolean;
  /** Optional action for opening original file/source */
  onViewSourceClick?: () => void;
  /** Whether clicking the trigger should open the source */
  triggerClickOpensSource?: boolean;
  /** Optional CTA text for source action button */
  viewSourceLabel?: string;
  /** Called when popover opens (hover start) */
  onOpen?: () => void;
  /** Called as soon as the pointer enters the trigger, before the open delay */
  onTriggerHover?: () => void;
  /** Optional classes for the popover panel */
  className?: string;
}

export interface SourcePopoverHeaderProps {
  source?: HeaderSource;
  pageInfo?: string;
}

const getPublicationYear = (date?: string): string | undefined => {
  if (!date) return undefined;
  const match = date.match(/^(\d{4})/);
  if (match) return match[1];

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? undefined : String(parsed.getFullYear());
};

export function SourcePopoverHeader({ source, pageInfo }: SourcePopoverHeaderProps) {
  const title = source?.title;
  const publicationYear = getPublicationYear(source?.publication_date ?? source?.date);
  const authority = source?.authority;
  const jurisdiction = source?.jurisdiction;
  const hasHeader = Boolean(title || pageInfo || publicationYear || authority || jurisdiction);
  if (!hasHeader) return null;

  return (
    <div className=" border-b border-border-subtle pb-1.5 flex-shrink-0">
      <div className="flex justify-between gap-2 items-center">
        {title && <div className="font-medium text-sm text-content-default">{title}</div>}
        {pageInfo && <div className="font-medium text-xs text-content-default">{pageInfo}</div>}
      </div>
      {(publicationYear || authority || jurisdiction) && (
        <div className="text-xs text-content-subtlest flex items-center flex-wrap mt-1">
          {publicationYear && <span>{publicationYear}</span>}
          {publicationYear && (authority || jurisdiction) && (
            <DotIcon size={14} weight="light" className="text-content-subtle" />
          )}
          {authority && <span>{formatDocumentAuthorityLabel(authority)}</span>}
          {authority && jurisdiction && <DotIcon size={14} weight="light" className="text-content-subtle" />}
          {jurisdiction && <span className="inline-flex items-center gap-1">{jurisdiction}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Shared hover preview for source content.
 * Used by both chat Citation and action extraction sources.
 */
export default function SourcePopover({
  content,
  contentPlaceholder,
  contentClassName,
  source,
  pageInfo,
  children,
  compactTrigger = true,
  clickable = false,
  onViewSourceClick,
  triggerClickOpensSource = false,
  viewSourceLabel = 'View original file',
  onOpen,
  onTriggerHover,
  className
}: SourcePopoverProps) {
  const posthog = usePostHog();
  const isFreemiumUser = useIsFreemiumUser();
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      const element = contentRef.current;
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [isOpen, content]);

  const hasHeader = Boolean(
    source?.title || source?.publication_date || source?.date || source?.authority || source?.jurisdiction || pageInfo
  );

  const handleViewSourceClick = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!onViewSourceClick) return;
    if (isFreemiumUser) {
      posthog?.capture('freemium_view_source_modal_clicked');
      setPremiumModalOpen(true);
      return;
    }
    onViewSourceClick();
  };

  return (
    <>
      <PreviewCard
        delay={150}
        closeDelay={150}
        triggerClassName={compactTrigger ? 'inline-block align-top h-5 ml-0.5' : 'inline align-baseline'}
        onTriggerClick={triggerClickOpensSource ? () => handleViewSourceClick() : undefined}
        onOpen={onOpen}
        onOpenChange={setIsOpen}
        className={twMerge('p-3 max-h-[50vh] max-w-[50ch] overflow-visible flex flex-col', className)}
        onTriggerHover={onTriggerHover}
        content={
          <>
            {hasHeader && <SourcePopoverHeader source={source} pageInfo={pageInfo} />}
            {content ? (
              <div
                ref={contentRef}
                className={`prose prose-sm !max-w-[50ch] text-xs overflow-hidden ${hasHeader ? 'mt-2' : ''} ${contentClassName ?? ''}`}
              >
                <SanitizedMarkdown>{content}</SanitizedMarkdown>
              </div>
            ) : (
              contentPlaceholder
            )}
            {isTruncated && (
              <div className="flex flex-col items-center text-content-subtle">
                <div>...</div>
                <div className="text-xs italic flex-shrink-0">
                  {clickable ? 'Contents truncated. Click to view full source.' : 'Contents truncated.'}
                </div>
              </div>
            )}
            {onViewSourceClick && (
              <div
                className={`flex justify-end pt-2 ${content || contentPlaceholder ? 'mt-2 border-t border-border-subtle' : ''}`}
              >
                <Button variant="outline" size="sm" text={viewSourceLabel} onClick={handleViewSourceClick} />
              </div>
            )}
          </>
        }
      >
        {children}
      </PreviewCard>
      <PremiumUpsellModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        featureDescription="With full access to CompliMind, you can view the original source documents in the Regulatory Wiki to verify information and explore related guidance."
      />
    </>
  );
}
